/**
 * Text normalization profiles.
 *
 * This is the highest-risk correctness area in the project: Hebrew is the
 * majority of CET content, and getting normalization wrong marks correct answers
 * incorrect — silently, and only for some students.
 *
 * So the rules are **numeric code-point ranges**, not regular expressions. Three
 * reasons, in order of importance:
 *
 *  1. `\p{Mn}` and `\s` resolve against the engine's Unicode data, which would
 *     make a grade depend on which browser or Node build ran it.
 *  2. A range written as `0x05b0, 0x05bd` with a comment naming it can be
 *     checked against the Unicode charts by a reviewer. A character class full of
 *     invisible combining marks cannot be read, reviewed, or safely edited — and
 *     it survives copy-paste through tooling only by luck.
 *  3. There is no clever matching here to justify a regex. It is a filter.
 *
 * The one impurity is `String.prototype.normalize`, which has no
 * dependency-free substitute. It is confined to a single call per profile and
 * recorded in `UNICODE_VERIFIED_AGAINST`, so it is the only route by which engine
 * data can reach a grade.
 */

import type { TextProfile } from '@cet/asset-contract';

/** Inclusive code-point range. */
type Range = readonly [number, number];

/**
 * Hebrew combining marks.
 *
 * Deliberately excluded because they are punctuation rather than marks, and a
 * reader would expect them preserved: 0x05be maqaf, 0x05c0 paseq, 0x05c3 sof
 * pasuq, 0x05c6 nun hafukha.
 *
 * Stripping te'amim is the behaviour Biblical content may need reversed — see
 * GAPS.md. That is a fourth profile when it is decided, not an edit here.
 */
const HEBREW_COMBINING_MARKS: readonly Range[] = [
  [0x0591, 0x05af], // te'amim (cantillation)
  [0x05b0, 0x05bd], // niqqud: sheva through meteg
  [0x05bf, 0x05bf], // rafe
  [0x05c1, 0x05c2], // shin dot, sin dot
  [0x05c4, 0x05c5], // upper dot, lower dot
  [0x05c7, 0x05c7], // qamats qatan
];

/**
 * Invisible characters. They arrive by copy-paste from RTL documents and would
 * otherwise make two identical-looking answers unequal.
 *
 * 0x200c ZWNJ and 0x200d ZWJ are orthographically meaningful in Arabic and
 * Persian. Stripping them is right for `he` and must be revisited when `ar`
 * arrives.
 */
const INVISIBLE: readonly Range[] = [
  [0x00ad, 0x00ad], // soft hyphen
  [0x061c, 0x061c], // Arabic letter mark
  [0x200b, 0x200f], // ZWSP, ZWNJ, ZWJ, LRM, RLM
  [0x202a, 0x202e], // LRE, RLE, PDF, LRO, RLO
  [0x2066, 0x2069], // LRI, RLI, FSI, PDI
  [0xfeff, 0xfeff], // BOM / zero-width no-break space
];

/**
 * Whitespace, enumerated rather than `\s`: the spec defines `\s` to include the
 * Unicode space-separator category, which is Unicode-version dependent.
 */
const WHITESPACE: readonly Range[] = [
  [0x0009, 0x000d], // tab, LF, VT, FF, CR
  [0x0020, 0x0020], // space
  [0x00a0, 0x00a0], // no-break space
  [0x1680, 0x1680], // Ogham space mark
  [0x2000, 0x200a], // en quad through hair space
  [0x2028, 0x2029], // line separator, paragraph separator
  [0x202f, 0x202f], // narrow no-break space
  [0x205f, 0x205f], // medium mathematical space
  [0x3000, 0x3000], // ideographic space
];

/**
 * Geresh, gershayim, and every quote character mistaken for them.
 *
 * Hebrew acronyms carry gershayim before the last letter — `צה"ל`, `תנ"ך` — and abbreviations
 * carry a geresh. The trouble is that four different characters *render identically*: the
 * Hebrew punctuation marks 0x05f3/0x05f4, the ASCII `'`/`"`, and the curly quotes Word
 * substitutes. An author types one, a learner's keyboard produces another, both look right on
 * screen, and the answer is marked wrong for a reason neither of them can see.
 *
 * **Stripped rather than mapped to a canonical mark**, so `צה"ל`, `צה״ל` and `צהל` all match.
 * The mark is punctuation, and no item is testing whether a learner produced the typographically
 * correct one.
 *
 * Note what this deliberately does NOT do: fold final letters (ם→מ, ן→נ, ץ→צ, ף→פ, ך→כ).
 * That is spelling, and whether `שלומ` counts for `שלום` depends on whether the item tests
 * geography or orthography — which only the author knows. An author who wants it lists the
 * variant in `accept`, which is already a list. No flag, no profile change.
 */
const HEBREW_QUOTE_MARKS: readonly Range[] = [
  [0x0022, 0x0022], // " quotation mark
  [0x0027, 0x0027], // ' apostrophe
  [0x02bc, 0x02bc], // ʼ modifier letter apostrophe
  [0x05f3, 0x05f4], // ׳ geresh, ״ gershayim
  [0x2018, 0x2019], // ‘ ’ curly single quotes
  [0x201c, 0x201d], // “ ” curly double quotes
];

const HEBREW_STRIP: readonly Range[] = [
  ...HEBREW_COMBINING_MARKS,
  ...HEBREW_QUOTE_MARKS,
  ...INVISIBLE,
];

function inRanges(codePoint: number, ranges: readonly Range[]): boolean {
  for (const [low, high] of ranges) {
    if (codePoint >= low && codePoint <= high) return true;
  }
  return false;
}

/**
 * Drop every character in `strip`, and collapse each run of whitespace to a
 * single space — with no leading or trailing space, in one pass.
 *
 * Leading whitespace never opens a pending space because nothing has been
 * emitted yet; trailing whitespace leaves one pending and it is never flushed.
 * That is the trim, and it means a value of only whitespace normalizes to `''` —
 * which the scorer treats as unanswered rather than wrong (D32).
 */
function scrub(input: string, strip: readonly Range[]): string {
  let out = '';
  let pendingSpace = false;

  for (const character of input) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (inRanges(codePoint, strip)) continue;

    if (inRanges(codePoint, WHITESPACE)) {
      pendingSpace = out.length > 0;
      continue;
    }

    if (pendingSpace) {
      out += ' ';
      pendingSpace = false;
    }
    out += character;
  }

  return out;
}

/**
 * `he` — NFC, strip niqqud and te'amim, strip geresh/gershayim and lookalike quotes, strip
 * invisibles, collapse whitespace.
 *
 * No case folding, which matters more than it looks: Hebrew content routinely
 * embeds Latin technical terms, so an item accepting `LRS` marks `lrs` wrong.
 * That is the profile as designed, and it is recorded as an open question in
 * GAPS.md rather than quietly changed here — grading semantics is not something
 * to adjust on the way past.
 *
 * One consequence of stripping the geresh: it also marks foreign sounds, so `ג'` (J) and `ג`
 * (G) become equal, as do `צ'` and `צ`. Nothing collides — `גירפה` is not a word — but an item
 * testing those letters specifically should not rely on the profile to police them.
 */
function normalizeHebrew(input: string): string {
  return scrub(input.normalize('NFC'), HEBREW_STRIP);
}

/**
 * `en` — NFC, strip invisibles, collapse whitespace, case fold.
 *
 * `toLowerCase`, never `toLocaleLowerCase`: the locale-sensitive form is where
 * Turkish dotless-i turns one engine's grade into another's, which is exactly why
 * the design bans `toLocale*`.
 */
function normalizeEnglish(input: string): string {
  return scrub(input.normalize('NFC'), INVISIBLE).toLowerCase();
}

/**
 * Strip invisibles and collapse whitespace, and nothing else — no NFC, no case folding, no
 * profile.
 *
 * For `numeric`, where the answer is digits. It needs the invisible stripping because an RTL
 * document pastes a right-to-left mark either side of a number and the parse would fail on a
 * character nobody can see; it needs the trim because a field's value arrives with whatever
 * spacing the learner left. It needs none of the rest.
 *
 * **It deliberately does not call `String.prototype.normalize`.** Digits are ASCII and NFC would
 * not move them, so skipping it means the numeric path has no dependence on the engine's Unicode
 * data at all — strictly better than the one impurity the text profiles have to accept.
 */
export function stripSpacing(input: string): string {
  return scrub(input, INVISIBLE);
}

export function normalize(input: string, profile: TextProfile): string {
  switch (profile) {
    case 'he':
      return normalizeHebrew(input);
    case 'en':
      return normalizeEnglish(input);
  }
}
